import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import nodeLibraryService from '../../../services/nodeLibraryService';
import nodeLibraryRealtimeService from '../../../services/nodeLibraryRealtimeService';

const NodeLibrary = ({ onNodeDragStart, isCollapsed, onToggleCollapse }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(['tasks']);
  const [nodeCategories, setNodeCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNodes();

    // Subscribe to real-time node changes
    const nodeChannel = nodeLibraryRealtimeService?.subscribeToNodeChanges((payload) => {
      console.log('Node library change detected:', payload?.eventType);
      // Reload nodes when any change occurs
      loadNodes();
    });

    // Subscribe to category changes
    const categoryChannel = nodeLibraryRealtimeService?.subscribeToNodeCategoryChanges((payload) => {
      console.log('Node category change detected:', payload?.eventType);
      // Reload nodes to update category names
      loadNodes();
    });

    // Cleanup subscriptions on unmount
    return () => {
      nodeLibraryRealtimeService?.unsubscribeAll([nodeChannel, categoryChannel]);
    };
  }, []);

  const loadNodes = async () => {
    try {
      setLoading(true);
      setError('');
      const nodes = await nodeLibraryService?.getAllNodes();
      
      // Group nodes by category
      const categoriesMap = {};
      nodes?.forEach(node => {
        if (!categoriesMap?.[node?.category]) {
          categoriesMap[node?.category] = {
            id: node?.category,
            name: node?.categoryName,
            icon: getCategoryIcon(node?.category),
            nodes: []
          };
        }
        categoriesMap?.[node?.category]?.nodes?.push(node);
      });
      
      setNodeCategories(Object.values(categoriesMap));
    } catch (err) {
      setError(err?.message || 'Failed to load nodes');
      console.error('Error loading nodes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      tasks: 'CheckSquare',
      resources: 'Users',
      conditions: 'GitBranch',
      connectors: 'Link'
    };
    return iconMap?.[category] || 'Box';
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev?.includes(categoryId)
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleDragStart = (e, node) => {
    e.dataTransfer.effectAllowed = 'copy';
    e?.dataTransfer?.setData('application/json', JSON.stringify(node));
    if (onNodeDragStart) {
      onNodeDragStart(node);
    }
  };

  const filteredCategories = nodeCategories?.map(category => ({
    ...category,
    nodes: category?.nodes?.filter(node =>
      node?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      node?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  }))?.filter(category => category?.nodes?.length > 0);

  if (isCollapsed) {
    return (
      <div className="w-16 h-full bg-card border-r border-border flex flex-col items-center py-4 gap-4">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Expand node library"
        >
          <Icon name="ChevronRight" size={20} />
        </button>
        {nodeCategories?.map(category => (
          <button
            key={category?.id}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-250"
            title={category?.name}
          >
            <Icon name={category?.icon} size={20} />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-card border-r border-border flex flex-col">
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Node Library</h2>
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Collapse node library"
        >
          <Icon name="ChevronLeft" size={18} />
        </button>
      </div>
      <div className="px-4 py-3 border-b border-border">
        <Input
          type="search"
          placeholder="Search nodes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full"
        />
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
            <p className="text-sm text-destructive mt-2 text-center">{error}</p>
            <button
              onClick={loadNodes}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filteredCategories?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground mt-2">No nodes found</p>
          </div>
        ) : (
          filteredCategories?.map(category => (
            <div key={category?.id} className="mb-4">
              <button
                onClick={() => toggleCategory(category?.id)}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-muted transition-colors duration-250"
              >
                <div className="flex items-center gap-2">
                  <Icon name={category?.icon} size={18} color="var(--color-muted-foreground)" />
                  <span className="text-sm font-medium text-foreground">{category?.name}</span>
                </div>
                <Icon
                  name={expandedCategories?.includes(category?.id) ? 'ChevronDown' : 'ChevronRight'}
                  size={16}
                  color="var(--color-muted-foreground)"
                />
              </button>

              {expandedCategories?.includes(category?.id) && (
                <div className="mt-2 space-y-2">
                  {category?.nodes?.map(node => (
                    <div
                      key={node?.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, node)}
                      className="flex items-start gap-3 px-3 py-3 rounded-lg bg-muted hover:bg-muted/80 cursor-move transition-all duration-250 hover:shadow-md"
                    >
                      <div
                        className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: `${node?.color}20`, color: node?.color }}
                      >
                        <Icon name={node?.icon} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-medium text-foreground">{node?.name}</h4>
                          <span className="caption text-muted-foreground px-2 py-1 rounded bg-background">
                            {node?.shortcut}
                          </span>
                        </div>
                        <p className="caption text-muted-foreground mt-1">{node?.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Info" size={14} />
          <span>Drag nodes to canvas</span>
        </div>
      </div>
    </div>
  );
};

export default NodeLibrary;